<template>
  <!-- Some quando não há o que oferecer: sem poder criar nem vincular, ou sem nada a vincular e sem
       poder criar. Um menu que abre vazio é promessa que não se cumpre. -->
  <!-- Um menu só, aberto de dois lugares: o botão do cabeçalho (o ativador) e o "+" de uma raia do
       quadro, que o abre ancorado nele (`openInLane`, com o `target`). -->
  <v-menu v-if="offersCreate || offersLink" v-model="menuOpen" :target="laneTarget || undefined"
          location="bottom end">
    <template #activator="{ props }">
      <ontos-header-action v-bind="props" icon="mdi-plus" color="primary" :label="$tt('relationship-add')"
                           @click="lane = null; laneTarget = null"/>
    </template>
    <v-list density="compact" nav>
      <!-- Os mesmos textos pelo cabeçalho e pela raia (escolha do usuário): o menu sai do "+" da
           raia, que já está à vista, e a restrição aparece na janela de vincular. -->
      <v-list-item v-if="offersCreate" prepend-icon="mdi-plus"
                   :title="$tt('instance-create', {collection: target.name})"
                   @click="creating = true"/>
      <v-list-item v-if="offersLink" prepend-icon="mdi-link-variant"
                   :title="$tt('relationship-link', {collection: target.name})"
                   @click="openLinking()"/>
    </v-list>
  </v-menu>

  <!-- A janela que cria e DEVOLVE o item, e não a da ficha: quem cria daqui quer vê-lo na seção, e
       não ir para a ficha dele. O dono vem preenchido e travado (`fixed-values`), no reverso; no
       múltiplo direto não há campo para travar — quem guarda a lista é o dono, e o vínculo vem depois. -->
  <ontos-create-instance-dialog v-if="creating"
                                :context="{system: ownerContext.system, idea: target.alias}"
                                :initial-values="lane ? lane.seed : null"
                                :fixed-values="fixedValues"
                                :known="createKnown"
                                :full-page="false"
                                @saved="onCreated"
                                @closed="creating = false"
                                @showMessage="$emit('showMessage', $event)"
                                @showErrorMessage="$emit('showErrorMessage', $event)"/>

  <ontos-link-existing-dialog v-if="linking"
                              :candidates="lane ? laneCandidates || [] : candidates"
                              :loading="lane !== null && laneCandidates === null"
                              :collection="target.pluralName"
                              :restriction="lane ? lane.values : []"
                              :target-context="{system: ownerContext.system, idea: target.alias}"
                              :title="$tt('relationship-link-title', {collection: target.pluralName})"
                              :subtitle="ownerName"
                              :saving="saving"
                              @link="commitLink($event)"
                              @closed="linking = false"/>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import Idea from '@/types/Idea'
import IdeaProperty from '@/types/IdeaProperty'
import {InstanceContext} from '@/types/InstanceContext'
import {EnhancedWorkpace} from '@/types/Workspace'
import {DomainItem, freeToLink, ReverseInfos} from '@/services/reverseRelationship'
import {
  linkToOwner, needsLinkAfterCreate, relatedIds, relationshipEditing, relationshipInitialValues
} from '@/utils/relationshipKanban'
import {knownForCreate} from '@/utils/instanceCreation'
import {LaneAdd} from '@/utils/kanbanDimensions'
import {Preview} from '@/utils/instancePreview'
import {collectionReadConfig} from '@/utils/collectionRead'
import {refuse} from '@/utils/refusal'
import CreateInstanceDialog from '@/components/live/instance/CreateInstanceDialog.vue'
import LinkExistingDialog from '@/components/live/instance/LinkExistingDialog.vue'

/**
 * O "Adicionar" de um relacionamento múltiplo: criar um item já vinculado ao dono, ou vincular itens
 * que já existem (pedido do usuário). Na Lista da seção da ficha, posto por ela; no quadro, posto
 * pelo próprio `RelationshipBoard` — na ficha e na página expandida, que é o mesmo quadro.
 *
 * - **Criar**: o item nasce apontando para o dono, sem poder tirá-lo. No reverso é o campo dele que
 *   aponta para cá, e vem preenchido e travado — inteiro, se for de valor único; só este dono, se for
 *   múltiplo. No múltiplo direto a lista mora no dono, e o vínculo é gravado depois de criar.
 * - **Vincular existente**: escolhe entre os que ainda não estão, vários de uma vez, e grava pelo
 *   dono (`linkToOwner`). Num reverso de dono único, roubar o item de outro dono pergunta antes.
 *
 * Daqui sobe o `added` com os itens que entraram, INTEIROS, e a ficha os põe na seção sem reler
 * nada (relato do usuário: tendo tudo na mão, não se vai ao servidor).
 */
@Component({
  emits: ['added', 'showMessage', 'showErrorMessage'],
  name: 'ontos-relationship-add',
  components: {
    'ontos-create-instance-dialog': CreateInstanceDialog,
    'ontos-link-existing-dialog': LinkExistingDialog
  }
})
class RelationshipAdd extends Vue {
  /** O dono: system, idea e instance da ficha. */
  @Prop({type: Object, required: true}) readonly ownerContext!: InstanceContext
  /** O dono carregado, com `_rels` — é dali que saem os já vinculados. */
  @Prop({type: Object, required: true}) readonly owner!: any
  @Prop({type: Object, required: true}) readonly property!: IdeaProperty
  /** Os candidatos de cada relacionamento do dono, por coleção — o `relDomains` da ficha. */
  @Prop({type: Object, default: () => ({})}) readonly domains!: { [ideaAlias: string]: DomainItem[] }
  /** O que o servidor contou sobre os reversos do dono, por alias. */
  @Prop({type: Object, default: null}) readonly reverseInfos!: ReverseInfos | null
  @Prop({required: true}) readonly workspace!: EnhancedWorkpace
  /**
   * O que o quadro desta seção já leu da coleção de destino (a resposta com `relDomains`), quando
   * leu: com ele o formulário de criar abre sem ir ao servidor. Ver o `knownForCreate`.
   */
  @Prop({type: Object, default: null}) readonly targetRead!: any

  creating = false
  linking = false
  saving = false

  menuOpen = false
  /**
   * A raia de onde se pediu, quando foi pelo "+" de uma raia do quadro: os valores com que o item
   * novo nasce nela, e quem mora nela. Null pelo botão do cabeçalho.
   */
  lane: LaneAdd | null = null
  /** O "+" da raia, onde o menu se ancora. Null pelo botão do cabeçalho, que é o próprio ativador. */
  laneTarget: Element | null = null
  /** Os que moram na raia e ainda não estão vinculados. Null enquanto a leitura não chega. */
  laneCandidates: DomainItem[] | null = null

  /**
   * Abre o menu no "+" de uma raia (ver o `addInLane` do KanbanBoard): criar nasce com os valores
   * dela, e vincular oferece só quem mora nela.
   */
  openInLane(element: Element, lane: LaneAdd) {
    this.lane = lane
    this.laneTarget = element
    this.menuOpen = true
  }

  /**
   * Vincular existentes. Pela raia, a escolha vem da coleção INTEIRA, e não dos candidatos que a
   * ficha já tem: estes trazem só nome e id, e saber quem mora na raia pede os valores de cada item
   * — "se a raia é Samba, aparece qualquer canção de samba, mesmo a que ainda não é deste compositor"
   * (pedido do usuário). Os já vinculados ficam de fora, pelo servidor.
   */
  async openLinking() {
    this.linking = true
    if (!this.lane) return
    this.laneCandidates = null
    const lane = this.lane
    try {
      const res = await this.$instances.list({systemAlias: this.ownerContext.system, ideaAlias: this.target.alias},
          collectionReadConfig(false, {_instance: {$nin: this.linked}}))
      this.laneCandidates = (res.value as any[]).filter(item => lane.accepts(item) && this.free(item))
    } catch (err) {
      this.laneCandidates = []
      refuse(err, this.ownerIdea)
    }
  }

  get target(): Idea {
    return this.workspace.ideaWith(this.property.relationship!.idea)!
  }

  get info() {
    return (this.reverseInfos && this.reverseInfos[this.property.alias]) || null
  }

  get ownerIdea(): Idea {
    return this.workspace.ideaWith(this.ownerContext.idea)!
  }

  /** O nome do dono, por extenso: o subtítulo da janela de vincular. */
  get ownerName(): string {
    return this.owner._identifier || ''
  }

  /** Quem pode o quê — a regra mora no `relationshipEditing`, a mesma do X de desvincular. */
  get offersCreate(): boolean {
    return relationshipEditing(this.workspace, this.owner, this.property, this.info).create
  }

  get offersLink(): boolean {
    return relationshipEditing(this.workspace, this.owner, this.property, this.info).link
  }

  get linked(): number[] {
    return relatedIds(this.owner, this.property)
  }

  get candidates(): DomainItem[] {
    return (this.domains[this.target.alias] || [])
        .filter(item => !this.linked.includes(Number(item._instance)) && this.free(item))
  }

  /** Pode vir para cá: o que já é de outro, com o campo de lá de um valor só, não se rouba (`freeToLink`). */
  free(item: DomainItem): boolean {
    return !this.info || freeToLink(item, this.info, Number(this.ownerContext.instance))
  }

  /** O dono, preenchido e travado no item novo — só no reverso, onde o item guarda o dono. */
  get fixedValues(): any {
    return relationshipInitialValues(this.property, this.ownerContext.instance!, this.info)
  }

  get createKnown(): any {
    const read = this.targetRead
    return read ? knownForCreate(this.workspace, this.target.alias, read.relDomains, read.reverseInfo) : null
  }

  /**
   * Criou: o item entra na seção como saiu do formulário — a prévia, com o id e o identificador que o
   * servidor deu e os calculados que o formulário já tinha pedido a ele. Nada a reler.
   */
  async onCreated(added: any, preview?: Preview) {
    this.creating = false
    try {
      if (needsLinkAfterCreate(this.property)) {
        await linkToOwner(this.$instances, this.ownerContext, this.owner, this.property, [Number(added._instance)])
      }
      this.$emit('added', [preview ? preview.value : added])
    } catch (err) {
      //O item foi criado e o vínculo não: o erro sobe, em vez de sumir, porque sobrou um item solto
      refuse(err, this.ownerIdea)
    }
  }

  async commitLink(ids: number[]) {
    this.saving = true
    try {
      await linkToOwner(this.$instances, this.ownerContext, this.owner, this.property, ids)
      const items = await this.itemsOf(ids)
      this.$emit('added', items)
      //A mensagem de salvo, como em toda gravação do app — até mudar um card de raia mostra a dele
      this.$emit('showMessage', this.$tt('info.relationship-linked',
          {name: items.map(item => item._identifier).join(', ')}))
      this.linking = false
    } catch (err) {
      //A recusa do servidor dita na língua da tela — a mesma frase da edição do dono —, na janela
      refuse(err, this.ownerIdea)
    } finally {
      this.saving = false
    }
  }

  /**
   * Os vinculados inteiros, para a seção desenhá-los — a linha e o card pedem os campos do item.
   *
   * Pela raia, já estão aqui: a escolha foi feita sobre a coleção lida inteira. Pelo cabeçalho a
   * ficha só tem nome e id dos candidatos, e a leitura é dos escolhidos, e só deles (escolha do
   * usuário) — a mesma forma da leitura da raia.
   */
  private async itemsOf(ids: number[]): Promise<any[]> {
    if (this.lane && this.laneCandidates) {
      return this.laneCandidates.filter(item => ids.includes(Number(item._instance)))
    }
    const res = await this.$instances.list({systemAlias: this.ownerContext.system, ideaAlias: this.target.alias},
        collectionReadConfig(false, {_instance: {$in: ids}}))
    return res.value as any[]
  }
}

export default toNative(RelationshipAdd)
</script>
